import React, { createContext, FC, useState } from 'react';
import { isEmpty, isEqual, merge, pickBy } from 'lodash';
import { Gql } from '@fiverr-private/dynamic_matching';
import { logger } from '@fiverr-private/obs';
import { DmBriefNeedType } from '@fiverr-private/dynamic_matching/dist/esm/src/gql-operations/gql-generated';
import { createUseContext } from '../../../../context/utils';
import { useBriefCreationContext } from '../../../../context/BriefCreationContext';
import { Budget, BudgetConstants, BudgetSchema, BudgetType } from '../../../../api';
import { BudgetAndTimelineContent } from './types';
import { buildFinalSectionContent } from './buildFinalSectionContent';
import { buildSectionContentStateFromContent, useNeedType } from './shared';

const fallbackBudgetConsts: BudgetConstants = {
  budget: {
    min: 80,
    validMin: 25,
    validMax: 50_000,
  },
  hourly_budget: {
    min: 0,
    validMin: 7,
    validMax: 10_000,
  },
  fixed_ongoing_budget: {
    min: 0,
    validMin: 5,
    validMax: Infinity,
  },
};

export interface BudgetAndTimelineContextProviderProps {
  onChange: (content: BudgetAndTimelineContent) => void;
  onEditEnd: () => void;
  onCancel: () => void;
  content: BudgetAndTimelineContent;
}

interface BudgetAndTimelineContextProps extends BudgetAndTimelineContextProviderProps {
  validMinBudget: number;
  validMaxBudget: number;
  recommendedMinBudget: number | null;
  sectionContentState: BudgetAndTimelineContent;
  setSectionContentState: (state: BudgetAndTimelineContent) => void;
  needType: Gql.DmBriefNeedType;
  isHourlyEligible: boolean;
  handlePricingTypeChange: (pricingType: Gql.BriefPricingType | null) => Promise<void>;
  hasChanges: boolean;
  ongoingWorkPotential: boolean;
  setOngoingWorkPotential: React.Dispatch<React.SetStateAction<boolean>>;
  additionalWorkPotentialAvailable: boolean;
}

const BudgetAndTimelineContext = createContext<BudgetAndTimelineContextProps | null>(null);

BudgetAndTimelineContext.displayName = 'BudgetAndTimelineContext';

export const BudgetAndTimelineContextProvider: FC<BudgetAndTimelineContextProviderProps> = ({
  children,
  onChange,
  onEditEnd,
  onCancel,
  content,
}) => {
  const { briefClassification, enableEnhancedUx } = useBriefCreationContext();
  const { needType } = useNeedType();
  const [sectionContentState, setSectionContentState] = useState<BudgetAndTimelineContent>(
    buildSectionContentStateFromContent(content, needType)
  );
  const [ongoingWorkPotential, setOngoingWorkPotential] = useState<boolean>(Boolean(content.ongoingWorkPotential));

  const { is_hourly_eligible: isHourlyEligible = false } = briefClassification?.static_data ?? {};
  const staticData = pickBy(briefClassification?.static_data ?? {}, (value) => !isEmpty(value));
  const budgetConsts: BudgetConstants = merge({}, fallbackBudgetConsts, staticData);

  const isFixedPriceMode: boolean = sectionContentState.pricingType === Gql.BriefPricingType.Fixed;
  const isPerDelivery: boolean = needType === DmBriefNeedType.Ongoing && isFixedPriceMode;

  const additionalWorkPotentialAvailable = enableEnhancedUx && needType === Gql.DmBriefNeedType.OneTime;

  let budgetType: BudgetType = 'budget';

  if (sectionContentState.pricingType === Gql.BriefPricingType.Hourly) {
    budgetType = 'hourly_budget';
  } else if (isPerDelivery) {
    budgetType = 'fixed_ongoing_budget';
  }

  const budget: Budget = budgetConsts[budgetType] ?? ({} as Budget);

  if (isEmpty(budget)) {
    logger.error(`No budget found for ${budgetType}, using fallback budget`, { briefClassification });
  }

  const parsedBudget = BudgetSchema.parse(budget);

  const { min: recommendedMinBudget, validMin: validMinBudget, validMax: validMaxBudget } = parsedBudget;

  const onEditEndWithFinalSectionContent = () => {
    const contentToSave = buildFinalSectionContent({
      sectionContent: sectionContentState,
      needType,
      enableEnhancedUx,
      ongoingWorkPotential,
    });

    onChange(contentToSave as BudgetAndTimelineContent);
    onEditEnd?.();
  };

  const handlePricingTypeChange = async (pricingType: Gql.BriefPricingType | null) => {
    if (!pricingType) return;

    const updatedSectionContentState = {
      ...sectionContentState,
      pricingType,
    };

    setSectionContentState(updatedSectionContentState);
  };

  const hasChanges = !isEqual(sectionContentState, buildSectionContentStateFromContent(content, needType));

  return (
    <BudgetAndTimelineContext.Provider
      value={{
        onChange,
        onEditEnd: onEditEndWithFinalSectionContent,
        onCancel,
        validMinBudget,
        validMaxBudget,
        recommendedMinBudget,
        content,
        sectionContentState,
        setSectionContentState,
        needType,
        isHourlyEligible,
        handlePricingTypeChange,
        hasChanges,
        ongoingWorkPotential,
        setOngoingWorkPotential,
        additionalWorkPotentialAvailable,
      }}
    >
      {children}
    </BudgetAndTimelineContext.Provider>
  );
};

export const useBudgetAndTimelineContext = createUseContext(BudgetAndTimelineContext);
